import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, X, Loader2, MapPin, Phone,
  Users, Store, Key
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { sucursalesService } from '@/services/sucursales.service';
import { empresasService } from '@/services/empresas.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';

interface BranchFormData {
  name: string;
  address: string;
  phone: string;
}

const emptyForm: BranchFormData = { name: '', address: '', phone: '' };

export function BranchesPage() {
  const { user } = useAuthStore();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { error, errorType, handleApiError: onApiError, handleAuthRedirect } = useApiError();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<any>(null);
  const [form, setForm] = useState<BranchFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const userRole = user?.role || '';

  // Load companies (SUPER_ADMIN sees all, ADMIN sees only theirs)
  useEffect(() => {
    if (userRole === 'SUPER_ADMIN') {
      empresasService.findAll()
        .then(setCompanies)
        .catch(() => {});
    } else if (user?.company?.id) {
      setCompanies([{ id: user.company.id, name: user.company.name, slug: user.company.slug }]);
      setSelectedCompanyId(user.company.id);
    }
  }, [userRole, user?.company?.id, user?.company?.name, user?.company?.slug]);

  const loadBranches = useCallback(async () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    try {
      const data = await sucursalesService.findByCompany(selectedCompanyId);
      setBranches(data || []);
    } catch (err: any) { onApiError(err); }
    setLoading(false);
  }, [selectedCompanyId]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  const openCreate = async () => {
    setEditingBranch(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = async (b: any) => {
    setEditingBranch(b);
    setForm({
      name: b.name || '',
      address: b.address || '',
      phone: b.phone || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedCompanyId || !form.name.trim()) return;
    setSaving(true);
    try {
      if (editingBranch) {
        await sucursalesService.update(editingBranch.id, {
          name: form.name,
          address: form.address || undefined,
          phone: form.phone || undefined,
        });
      } else {
        await sucursalesService.create({
          companyId: selectedCompanyId,
          name: form.name,
          address: form.address || undefined,
          phone: form.phone || undefined,
        });
      }
      setModalOpen(false);
      loadBranches();
    } catch (err: any) { onApiError(err); }
    setSaving(false);
  };

  const handleDeactivate = async (b: any) => {
    if (!await dialogs.confirm(`¿Desactivar la sucursal "${b.name}"?`)) return;
    try {
      await sucursalesService.deactivate(b.id);
      loadBranches();
    } catch (err: any) { onApiError(err); }
  };

  const handleGenerateToken = async (b: any) => {
    if (!await dialogs.confirm(`¿Generar nuevo token de activación para "${b.name}"? El token anterior dejará de funcionar.`)) return;
    try {
      await sucursalesService.generateToken(b.id);
      loadBranches();
    } catch (err: any) { onApiError(err); }
  };

  const filtered = branches.filter((b: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.name?.toLowerCase().includes(q) ||
      b.address?.toLowerCase().includes(q) ||
      b.phone?.toLowerCase().includes(q)
    );
  });

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadBranches} onLogin={handleAuthRedirect} />
      {!error && (
      <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Sucursales</h1>
          <p className="text-sm text-ios-gray mt-1">
            {selectedCompany
              ? `${branches.length} sucursales en ${selectedCompany.name}`
              : 'Selecciona una empresa'}
          </p>
        </div>
        {selectedCompanyId && (
          <button onClick={openCreate} className="ios-button flex items-center gap-2 px-4 py-2">
            <Plus size={16} />
            Nueva Sucursal
          </button>
        )}
      </div>

      {/* Company selector (only SUPER_ADMIN) */}
      {userRole === 'SUPER_ADMIN' && (
        <div className="mb-4">
          <label className="text-xs text-ios-gray mb-1.5 block font-medium">Empresa</label>
          <select
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="ios-input max-w-md"
          >
            <option value="">Seleccionar empresa...</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>{c.name} (@{c.slug})</option>
            ))}
          </select>
        </div>
      )}

      {/* Search */}
      {selectedCompanyId && (
        <div className="relative max-w-sm mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar sucursal..."
            className="ios-input w-full pl-9 h-9 text-sm"
          />
        </div>
      )}

      {/* Branches list */}
      {!selectedCompanyId ? (
        <div className="ios-card p-12 text-center">
          <Store size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">Selecciona una empresa</h3>
          <p className="text-sm text-ios-gray">Elige una empresa del selector para ver sus sucursales</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 size={24} className="animate-spin text-ios-gray" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Store size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">
            {search ? 'Sin resultados' : 'No hay sucursales'}
          </h3>
          <p className="text-sm text-ios-gray mb-4">
            {search ? 'Intenta con otros términos' : 'Crea tu primera sucursal'}
          </p>
          {!search && (
            <button onClick={openCreate} className="ios-button">
              + Nueva Sucursal
            </button>
          )}
        </div>
      ) : (
        <div className="ios-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Sucursal</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Dirección / Teléfono</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Estado</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Token</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Usuarios</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-5">
                {filtered.map((b: any) => (
                  <tr
                    key={b.id}
                    className="hover:bg-ios-gray-6/30 transition-colors cursor-pointer"
                    onClick={() => openEdit(b)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-400/20 to-teal-200 rounded-[10px] flex items-center justify-center flex-shrink-0">
                          <Store size={16} className="text-emerald-600" />
                        </div>
                        <span className="font-semibold text-ios-dark">{b.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {b.address && (
                          <span className="text-xs text-ios-gray flex items-center gap-1.5">
                            <MapPin size={12} /> {b.address}
                          </span>
                        )}
                        {b.phone && (
                          <span className="text-xs text-ios-gray flex items-center gap-1.5">
                            <Phone size={12} /> {b.phone}
                          </span>
                        )}
                        {!b.address && !b.phone && <span className="text-xs text-ios-gray-4">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        b.isActive === false ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'
                      }`}>
                        {b.isActive === false ? 'Inactiva' : 'Activa'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {b.activationToken ? (
                        <span className="text-xs font-mono bg-blue-50 text-blue-600 px-2 py-1 rounded">
                          {b.activationToken}
                        </span>
                      ) : (
                        <span className="text-xs text-ios-gray-4">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-ios-gray flex items-center justify-end gap-1.5">
                        <Users size={12} /> {b._count?.users || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGenerateToken(b); }}
                          className="text-ios-gray hover:text-blue-500 p-1.5 rounded-lg hover:bg-blue-50 transition-colors inline-flex"
                          title="Generar Token"
                        >
                          <Key size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeactivate(b); }}
                          className="text-ios-gray hover:text-ios-red p-1.5 rounded-lg hover:bg-red-50 transition-colors inline-flex"
                          title="Desactivar"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">
                {editingBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Nombre *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Sucursal Centro"
                  className="ios-input w-full"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">Dirección</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
                  placeholder="Av. Reforma 123, Col. Centro"
                  className="ios-input w-full"
                />
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">Teléfono</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+52..."
                  className="ios-input w-full"
                />
              </div>

              {editingBranch && (
                <p className="text-xs text-ios-gray">
                  Empresa: {selectedCompany?.name || editingBranch.company?.name}
                </p>
              )}
            </div>

            <div className="p-4 border-t border-ios-gray-5 flex gap-2">
              <button
                onClick={() => setModalOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !form.name.trim()}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Guardando...' : editingBranch ? 'Actualizar' : 'Crear Sucursal'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
